import { z } from 'zod';

import { alphanumericRegex, integerAndFloatNumbersWithNegativesRegex } from 'constants/regex';

const amountMaxValue = 99999.99;

function isFutureDate(date: Date): boolean {
  const currentDate = new Date();
  return date.getTime() > currentDate.getTime();
}

const validationSchema = z.object({
  roofTopId: z
    .string({ required_error: 'Please select a dealership' })
    .min(1, 'Please select a dealership')
    .uuid({ message: 'Please select a dealership' }),
  roNumber: z
    .string()
    .min(1, 'Please enter a valid RO number')
    .regex(alphanumericRegex, { message: 'Only numbers or letters allowed' }),
  roDate: z
    .date({
      required_error: 'Please enter a valid RO open date',
      invalid_type_error: 'Please enter a valid RO open date',
    })
    .refine(selectedDate => !isFutureDate(selectedDate), {
      message: "Future date isn't allowed",
    }),
  claimSubmittedDate: z
    .date()
    .nullable()
    .optional()
    .refine(selectedDate => !selectedDate || !isFutureDate(selectedDate)),
  lineNumber: z.string().nullable().optional(),
  techNumber: z
    .string({ required_error: 'Please enter a tech number' })
    .min(1, 'Please enter a tech number'),
  advisorNumber: z
    .string({ required_error: 'Please enter an advisor number' })
    .min(1, 'Please enter an advisor number'),
  vin: z.string().nullable().optional(),
  amount: z
    .string()
    .min(1, 'Please enter a valid amount')
    .regex(integerAndFloatNumbersWithNegativesRegex, {
      message: 'Only integer or decimal numbers allowed',
    })
    .refine(
      val => {
        const amountValue = parseFloat(val);
        return amountValue <= amountMaxValue;
      },
      { message: 'Please enter an amount less than or equal to 99999.99' }
    )
    .refine(
      val => {
        const decimalPart = val.split('.')[1];
        return !decimalPart || decimalPart.length <= 2;
      },
      { message: 'Please enter an amount with no more than 2 decimal places' }
    ),
  requestCategoryId: z
    .number({ required_error: 'Please select a request category' })
    .min(0, 'Please select a request category'),
  requestSubcategoryId: z.number().nullable().optional(),
  requestSubCategoryName: z.string().nullable().optional(),
  claimStatusId: z
    .number({ required_error: 'Please select a claim status' })
    .min(0, 'Please select a claim status'),
  ticketPriorityId: z
    .number({ required_error: 'Please select a priority' })
    .min(0, 'Please select a priority'),
  text: z.string().nullable(),
});

export default validationSchema;
